import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, CreditCard, FileText, Loader2, XCircle } from 'lucide-react';
import { Button, Card } from './charte';
import {
  confirmCheckout,
  createInvoiceCheckout,
  fetchPayableInvoices,
} from '../api/candidate';
import { formatEuro } from '../lib/format';
import dayjs from '../lib/dates';

/** Bannière de retour depuis Stripe (?payment=success|cancelled). */
function usePaymentReturn(onSettled: () => void) {
  const [banner, setBanner] = useState<'success' | 'cancelled' | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const state = params.get('payment');
    const sessionId = params.get('session_id');
    if (state === 'success' || state === 'cancelled') {
      setBanner(state);
      params.delete('payment');
      params.delete('invoice');
      params.delete('session_id');
      const qs = params.toString();
      window.history.replaceState(
        {},
        '',
        window.location.pathname + (qs ? `?${qs}` : ''),
      );
      if (state === 'success') {
        // Réconciliation immédiate au retour : on confirme la session Stripe
        // (officialise la facture sans attendre le webhook), puis on rafraîchit.
        if (sessionId) {
          confirmCheckout(sessionId)
            .catch(() => undefined)
            .finally(onSettled);
        } else {
          onSettled();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return { banner, dismiss: () => setBanner(null) };
}

/**
 * Factures à régler par l'élève + règlement en ligne (Stripe Checkout).
 * Affiché sur le tableau de bord (toujours visible) et dans « Mes paiements ».
 */
export default function PayableInvoices() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['candidate', 'payable-invoices'],
    queryFn: fetchPayableInvoices,
  });

  const { banner, dismiss } = usePaymentReturn(() => {
    queryClient.invalidateQueries({ queryKey: ['candidate', 'payable-invoices'] });
    queryClient.invalidateQueries({ queryKey: ['candidate', 'payments'] });
  });

  const [pendingId, setPendingId] = useState<string | null>(null);
  const checkout = useMutation({
    mutationFn: (invoiceId: string) => createInvoiceCheckout(invoiceId),
    onMutate: (invoiceId) => setPendingId(invoiceId),
    onSuccess: ({ url }) => {
      window.location.href = url; // redirection vers Stripe Checkout
    },
    onError: () => setPendingId(null),
  });

  const invoices = data?.invoices ?? [];
  const paymentEnabled = data?.paymentEnabled ?? false;

  return (
    <>
      {banner && (
        <div
          className={`rounded-row p-4 flex items-center gap-3 ${
            banner === 'success'
              ? 'bg-success-light text-success-dark'
              : 'bg-warn-soft text-warn-fg'
          }`}
        >
          {banner === 'success' ? <CheckCircle2 size={18} /> : <XCircle size={18} />}
          <span className="text-sm font-medium">
            {banner === 'success'
              ? 'Paiement effectué, merci ! Votre auto-école a été créditée.'
              : 'Paiement annulé — aucune somme n’a été prélevée.'}
          </span>
          <button type="button" onClick={dismiss} className="ml-auto text-xs underline">
            Fermer
          </button>
        </div>
      )}

      {(isLoading || invoices.length > 0) && (
        <Card>
          <h2 className="text-title-sm text-heading">Factures à régler</h2>
          <p className="mt-1 mb-4 text-sm text-secondary">
            Payez vos factures en ligne par carte bancaire, en toute sécurité.
          </p>

          {isLoading ? (
            <div className="flex items-center text-secondary text-sm py-2">
              <Loader2 className="animate-spin mr-2" size={16} /> Chargement…
            </div>
          ) : !paymentEnabled ? (
            <p className="text-sm text-secondary">
              Le paiement en ligne n’est pas encore activé par votre auto-école.
            </p>
          ) : (
            <div className="divide-y divide-slate-100">
              {invoices.map((inv) => (
                <div key={inv.id} className="flex flex-wrap items-center gap-x-3 gap-y-3 py-3.5 first:pt-0 last:pb-0">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-surface-indigo text-primary">
                    <FileText size={18} aria-hidden />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-heading truncate">
                      {inv.title}
                    </div>
                    <div className="text-xs text-secondary">
                      {inv.number} · {dayjs(inv.issueDate).format('DD/MM/YYYY')}
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-heading flex-shrink-0">
                    {formatEuro(inv.totalTtc)}
                  </div>
                  {/* Sur téléphone, le bouton passe sous la ligne, sur toute la largeur. */}
                  <Button
                    size="sm"
                    variant="soft"
                    icon={CreditCard}
                    onClick={() => checkout.mutate(inv.id)}
                    loading={pendingId === inv.id}
                    className="max-sm:w-full"
                  >
                    Régler la facture
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </>
  );
}
